import './theme.css';
import type { LocalizedNavItem } from '@/app/lib/stage/nav-model.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import { ShivurtHeader } from './ShivurtHeader.tsx';
import { resolveInitialAppearance } from './theme-mode.ts';
import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

export interface SiteConfig {
  brandName: string;
  navItems: LocalizedNavItem[];
  defaultChips: MessageDescriptor[];
  /** Page-area mode: 'site' = the latest surface IS the page; 'chat' = a
   *  scrolling transcript with inline surfaces. Same shell either way. */
  mode: 'site' | 'chat';
  /** Site header (brand + menu). Off = minimal canvas: the rail keeps
   *  history, chips keep the standing intents, the dock keeps input.
   *  Template default is off; a branded site turns it on. */
  showHeader: boolean;
  /** Custom header component. Omit for the template's default header
   *  (monogram + name + menu); provide one for a bespoke composition —
   *  it receives resolved nav items + onNavigate, so menu mechanics and
   *  the agent's /nav overlay keep working. */
  Header?: FC<AgentHeaderProps>;
  /** First-load pending state shows the brand name (shimmer) instead of the
   *  neutral text skeleton — for agents with a website identity. */
  brandedArrival?: boolean;
  /** The site's designed look — a builder decision, like a real website's.
   *  'auto' follows the visitor's OS. */
  appearance: 'light' | 'dark' | 'auto';
  /** Omnibox placeholder override. */
  placeholder?: MessageDescriptor;
}

const siteMessages = defineMessages({
  sourceLabel: {
    id: 'nav.source.label',
    defaultMessage: 'Source',
    description: 'Short top-navigation destination label for starting a sourcing request.',
  },
  sourceIntent: {
    id: 'nav.source.intent',
    defaultMessage: 'I need to source something',
    description: 'Message sent to the agent when the buyer chooses Source.',
  },
  suppliersLabel: {
    id: 'nav.suppliers.label',
    defaultMessage: 'Suppliers',
    description: 'Short top-navigation destination label for the verified supplier network.',
  },
  suppliersIntent: {
    id: 'nav.suppliers.intent',
    defaultMessage: 'Tell me about your verified supplier network',
    description: 'Message sent to the agent when the buyer chooses Suppliers.',
  },
  verificationLabel: {
    id: 'nav.verification.label',
    defaultMessage: 'Verification',
    description: 'Short top-navigation destination label for how supplier verification works.',
  },
  verificationIntent: {
    id: 'nav.verification.intent',
    defaultMessage: 'How does supplier verification work?',
    description: 'Message sent to the agent when the buyer chooses Verification.',
  },
  humanLabel: {
    id: 'nav.accountManager.label',
    defaultMessage: 'Talk to a person',
    description: 'Short top-navigation destination label for human handoff.',
  },
  humanIntent: {
    id: 'nav.accountManager.intent',
    defaultMessage: 'I would like a Shivurt account manager to handle this',
    description: 'Message sent to the agent when the buyer asks for a human account manager.',
  },
  startSourcingChip: {
    id: 'home.chip.startSourcing',
    defaultMessage: 'Start a sourcing request',
    description: 'Standing suggestion chip that opens the sourcing intake.',
  },
  verificationChip: {
    id: 'home.chip.verification',
    defaultMessage: 'How do you verify suppliers?',
    description: 'Standing suggestion chip asking how verification tiers work.',
  },
  askPlaceholder: {
    id: 'composer.procurementPrompt',
    defaultMessage: 'Describe what you need to buy…',
    description: 'Invitation shown inside the empty composer.',
  },
});

export const SITE_CONFIG: SiteConfig = {
  brandName: 'Shivurt',
  Header: ShivurtHeader,
  navItems: [
    { label: siteMessages.sourceLabel, intent: siteMessages.sourceIntent },
    { label: siteMessages.suppliersLabel, intent: siteMessages.suppliersIntent },
    { label: siteMessages.verificationLabel, intent: siteMessages.verificationIntent },
    { label: siteMessages.humanLabel, intent: siteMessages.humanIntent },
  ],
  defaultChips: [siteMessages.startSourcingChip, siteMessages.verificationChip],
  mode: 'site',
  showHeader: true,
  brandedArrival: true,
  /** The visitor's own light/dark choice for this session, falling back to
   *  their OS. The header's toggle moves it from here on. */
  appearance: resolveInitialAppearance(),
  placeholder: siteMessages.askPlaceholder,
};
